import Vue from 'vue';
import VueRouter from 'vue-router';
import VueMeta from 'vue-meta';
import axios from 'axios';
import routes from './routes';
import Store from '@/state/store';
import i18n from '../i18n';
import { redirectIfPreview } from '@/utils/previewRedirect';

Vue.use(VueRouter);
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
});

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  if (routeTo.meta.authRequired) {
    let msal = router.app.$msal;
    if (msal.currentAccount == null) {
      Store.commit('user/setLoadingUserConfig', true);
      msal.init().then(() => {
        Store.commit('user/setLoadingUserConfig', false);
        verificationBeforeChange(routeTo, next, msal);
      });
    } else {
      verificationBeforeChange(routeTo, next, msal);
    }
  } else {
    if (routeTo.params.lang != undefined) {
      i18n.locale = routeTo.params.lang;
    } else if (routeTo.query.lang != undefined) {
      i18n.locale = routeTo.query.lang;
    } else {
      i18n.locale = process.env.VUE_APP_I18N_FALLBACK_LOCALE;
    }

    // External/public links: check if the organisation (from the URL param)
    // should be routed to the preview deployment, and redirect if so.
    if (routeTo.params.orgcode) {
      try {
        const envRes = await axios.get(
          `${process.env.VUE_APP_API_URL}application/public/${routeTo.params.orgcode}/environment`
        );
        if (redirectIfPreview(envRes.data.environment)) {
          return; // navigating away to preview host
        }
      } catch (e) {
        // fail open: stay on the current environment
        // eslint-disable-next-line no-console
        console.log(e);
      }
    }

    next();
  }
});

function verificationBeforeChange(to, next, msal) {
  if (msal.currentAccount != null) {
    if (to.meta.adminOnly) {
      if (Store.state.user.config != null && Store.state.user.config.isAdmin) {
        next();
      } else {
        next({ name: 'default' });
      }
    } else {
      if (to.meta.role) {
        if (
          Store.state.user.config != null &&
          ((to.meta.adminAccess && Store.state.user.config.isAdmin) ||
            Store.state.user.config.permissions[to.meta.role])
        ) {
          next();
        } else {
          next({ name: 'default' });
        }
      } else {
        next();
      }
    }
  } else {
    next({ name: 'default' });
  }
}

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  // If we reach this point, continue resolving the route.
  next();
});

export default router;
